import type { OrgEnablement, PartnerClient } from '@epilot/sdk/identity'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { OrganizationPicker } from './organization-picker'
import { errorMessage, ScopePicker } from './shared'
import { organizationSchema, SCOPES } from './validation'

import { identityClient } from '@/api/identity-client'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/use-toast'
import { useRefreshIdentity } from '@/services/identity.service'

const organizationFormSchema = z.object({
  org_id: z.string(),
  allowed_scopes: z.array(z.enum(SCOPES)),
  customer_approval_ref: z.string()
})

type OrganizationFormValues = z.infer<typeof organizationFormSchema>

const toInput = (
  values: OrganizationFormValues
): z.input<typeof organizationSchema> => ({
  org_id: values.org_id.trim(),
  authorization_policy: {
    allowed_scopes: values.allowed_scopes,
    // approved_at is set by the API: now when enabling, unchanged on a policy edit
    customer_approval_ref: values.customer_approval_ref.trim()
  }
})

const formSchemaFor = (client: PartnerClient) =>
  organizationFormSchema.superRefine((values, ctx) => {
    const result = organizationSchema.safeParse(toInput(values))

    if (!result.success) {
      for (const issue of result.error.issues) {
        const field =
          issue.path[0] === 'authorization_policy'
            ? String(issue.path[1])
            : String(issue.path[0] ?? 'org_id')

        ctx.addIssue({
          code: 'custom',
          path: [field],
          message: issue.message
        })
      }
    }
    if (
      values.allowed_scopes.some(
        (scope) => !client.scopes_allowed.includes(scope)
      )
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['allowed_scopes'],
        message: 'Allowed scopes must be a subset of the application’s scopes.'
      })
    }
  })

export const OrganizationForm = ({
  client,
  organization,
  onSaved
}: {
  client: PartnerClient
  organization?: OrgEnablement
  onSaved: () => void
}) => {
  const refresh = useRefreshIdentity()
  // A revoked enablement is enabled again with fresh approval evidence, not edited.
  const editing = organization && organization.status !== 'revoked'
  const original = editing ? organization : undefined
  const schema = useMemo(() => formSchemaFor(client), [client])
  const form = useForm<OrganizationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      org_id: organization?.org_id ?? '',
      allowed_scopes:
        organization?.authorization_policy.allowed_scopes ??
        client.scopes_allowed,
      customer_approval_ref:
        original?.authorization_policy.customer_approval_ref ?? ''
    },
    mode: 'onChange'
  })
  const busy = form.formState.isSubmitting

  const onSubmit = async (values: OrganizationFormValues) => {
    const input = organizationSchema.parse(toInput(values))

    try {
      if (original) {
        const { org_id, ...update } = input

        await identityClient.updateOrganization(client.client_id, org_id, {
          ...update,
          expected_policy_version: original.authorization_policy.version
        })
        toast({ title: 'Policy saved' })
      } else {
        await identityClient.enableOrganization(client.client_id, input)
        toast({ title: 'Organization enabled' })
      }
      void refresh()
      onSaved()
    } catch (error) {
      toast({
        title: 'Error',
        description: `Could not save the organization. ${errorMessage(error)}`,
        variant: 'destructive'
      })
    }
  }

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset className="space-y-4" disabled={busy}>
          <FormField
            control={form.control}
            name="org_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization</FormLabel>
                <FormControl>
                  <OrganizationPicker
                    disabled={Boolean(organization) || busy}
                    id="org_id"
                    onChange={field.onChange}
                    value={field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="allowed_scopes"
            render={({ field }) => (
              <FormItem>
                <ScopePicker
                  allowed={client.scopes_allowed}
                  disabled={busy}
                  onChange={field.onChange}
                  value={field.value}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="customer_approval_ref"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Customer approval reference</FormLabel>
                <FormControl>
                  <Input maxLength={512} {...field} />
                </FormControl>
                <FormDescription>
                  A ticket, signed order or contract reference recording the
                  customer’s approval.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <p className="text-sm text-muted-foreground">
            Enables sign-in for all current members of this organization. The
            partner maps the organization to its own tenant by this organization
            ID.
          </p>
          <Button loading={busy} type="submit">
            {original ? 'Save policy' : 'Enable organization'}
          </Button>
        </fieldset>
      </form>
    </Form>
  )
}
